import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';
import { toPaise } from '@vns/core';
import { useSession } from './session';
import { deleteProjectCascade, deleteClientCascade } from './cascade-delete';

/**
 * Data access.
 *
 * Note what is absent from every query below: any filter on company_id. That is
 * deliberate — Row Level Security adds it inside the database, so it cannot be
 * forgotten here. A query that looks unscoped is still scoped.
 */

/**
 * Supabase returns { data, error } rather than throwing, and `data` is nullable
 * even on success. Turning both into exceptions here means React Query sees a
 * real failure, and callers get a value that is genuinely non-null — rather
 * than a null that only crashes further down where the cause is unclear.
 */
function unwrap<T>({ data, error }: { data: T; error: { message: string } | null }): NonNullable<T> {
  if (error) throw new Error(error.message);
  if (data === null || data === undefined) throw new Error('No data returned');
  return data as NonNullable<T>;
}

export const keys = {
  dashboard: ['dashboard'] as const,
  clients: (search?: string) => ['clients', search ?? ''] as const,
  client: (id: string) => ['client', id] as const,
  projectsOfClient: (clientId: string) => ['projects', 'client', clientId] as const,
};

/* ------------------------------- dashboard ------------------------------- */

export interface DashboardData {
  activeClients: number;
  activeProjects: number;
  totalProjects: number;
  /** Projects still owing money with complete budget and payment breakdowns. */
  pending: Array<{
    projectId: string;
    projectName: string;
    clientName: string;
    valuePaise: number;
    paidPaise: number;
    balancePaise: number;
  }>;
  recentPayments: Array<{
    id: string;
    paidOn: string;
    amountPaise: number;
    paymentType: string;
    projectName: string;
    clientName: string;
    referenceNo: string | null;
  }>;
}

export function useDashboard() {
  return useQuery({
    queryKey: keys.dashboard,
    queryFn: async (): Promise<DashboardData> => {
      const [clientsRes, projectsRes, paymentsRes] = await Promise.all([
        supabase.from('clients').select('id', { count: 'exact', head: true }).is('deleted_at', null),
        supabase
          .from('projects')
          .select('id, name, status, value_paise, clients(name)')
          .is('deleted_at', null),
        supabase
          .from('client_payments')
          .select('id, project_id, paid_on, amount_paise, payment_type, reference_no, projects(name, clients(name))')
          .eq('approval_status', 'approved')
          .is('deleted_at', null)
          .order('paid_on', { ascending: false })
          .limit(10),
      ]);
      if (clientsRes.error) throw new Error(clientsRes.error.message);
      const projects = unwrap(projectsRes);

      // Fetch all payments for totals
      const { data: allPayments, error: payErr } = await supabase
        .from('client_payments')
        .select('project_id, amount_paise')
        .eq('approval_status', 'approved')
        .is('deleted_at', null);
      if (payErr) throw new Error(payErr.message);

      const paidByProject = new Map<string, number>();
      for (const p of allPayments ?? []) {
        paidByProject.set(p.project_id, (paidByProject.get(p.project_id) ?? 0) + Number(p.amount_paise));
      }

      const pending = projects
        .filter((p) => p.status === 'ongoing' || p.status === 'on_hold')
        .map((p) => {
          const paid = paidByProject.get(p.id) ?? 0;
          return {
            projectId: p.id,
            projectName: p.name,
            clientName: (p.clients as unknown as { name: string } | null)?.name ?? '—',
            valuePaise: Number(p.value_paise),
            paidPaise: paid,
            balancePaise: Number(p.value_paise) - paid,
          };
        })
        .filter((p) => p.balancePaise > 0)
        .sort((a, b) => b.balancePaise - a.balancePaise);

      const recentPayments = (paymentsRes.data ?? []).map((p) => {
        const proj = p.projects as unknown as { name?: string; clients?: { name?: string } | null } | null;
        return {
          id: p.id,
          paidOn: p.paid_on,
          amountPaise: Number(p.amount_paise),
          paymentType: p.payment_type,
          projectName: proj?.name ?? '—',
          clientName: proj?.clients?.name ?? '—',
          referenceNo: p.reference_no,
        };
      });

      return {
        activeClients: clientsRes.count ?? 0,
        activeProjects: projects.filter((p) => p.status === 'ongoing').length,
        totalProjects: projects.length,
        pending,
        recentPayments,
      };
    },
  });
}

/* -------------------------------- clients -------------------------------- */

export interface ClientRow {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  city: string | null;
  address: string | null;
  projectCount: number;
}

export function useClients(search: string) {
  return useQuery({
    queryKey: keys.clients(search),
    queryFn: async (): Promise<ClientRow[]> => {
      let q = supabase
        .from('clients')
        .select('id, name, phone, email, city, address, projects(count)')
        .is('deleted_at', null)
        .order('name');

      const term = search.trim();
      if (term) {
        // PostgREST reads commas as OR separators, so they must not survive
        // into the pattern or a search for "Kumar, Chennai" becomes two filters.
        const safe = term.replace(/[%,()]/g, ' ');
        q = q.or(`name.ilike.%${safe}%,phone.ilike.%${safe}%,city.ilike.%${safe}%`);
      }

      const rows = unwrap(await q);
      return rows.map((r) => ({
        id: r.id,
        name: r.name,
        phone: r.phone,
        email: r.email,
        city: r.city,
        address: r.address,
        projectCount: (r.projects as unknown as Array<{ count: number }>)?.[0]?.count ?? 0,
      }));
    },
  });
}

export interface NewClient {
  name: string;
  phone: string;
  email?: string;
  address?: string;
  city?: string;
}

export function useCreateClient() {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);

  return useMutation({
    mutationFn: async (input: NewClient) => {
      if (!actor) throw new Error('Not signed in');
      return unwrap(
        await supabase
          .from('clients')
          .insert({
            company_id: actor.companyId,
            name: input.name.trim(),
            phone: input.phone.trim(),
            email: input.email?.trim() || null,
            address: input.address?.trim() || null,
            city: input.city?.trim() || null,
            created_by: actor.id,
          })
          .select('id')
          .single(),
      );
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['clients'] }),
        qc.invalidateQueries({ queryKey: ['lookups'] }),
        qc.invalidateQueries({ queryKey: keys.dashboard }),
      ]);
    },
  });
}

/* -------------------------------- projects -------------------------------- */

export type ProjectStatus = 'planning' | 'ongoing' | 'on_hold' | 'completed';

export interface ProjectRow {
  id: string;
  name: string;
  location: string | null;
  status: ProjectStatus;
  startDate: string | null;
  valuePaise: number;
  paidPaise: number;
  pendingPaise: number;
  clientId: string;
  clientName: string;
}

/**
 * Ongoing first, completed last — the requirements are explicit about the
 * order. Sorting happens here rather than in SQL because the status ranking is
 * a product decision, not a database one.
 */
const STATUS_RANK: Record<ProjectStatus, number> = {
  ongoing: 0, on_hold: 1, planning: 2, completed: 3,
};

export function useProjects(
  search: string,
  status: string,
  clientId?: string,
  projectId?: string,
) {
  return useQuery({
    queryKey: ['projects', search, status, clientId ?? '', projectId ?? ''],
    queryFn: async (): Promise<ProjectRow[]> => {
      let q = supabase
        .from('projects')
        .select('id, name, location, status, start_date, value_paise, client_id, clients(name)')
        .is('deleted_at', null);

      if (status) q = q.eq('status', status);
      if (clientId) q = q.eq('client_id', clientId);
      if (projectId) q = q.eq('id', projectId);

      const term = search.trim();
      if (term) {
        const safe = term.replace(/[%,()]/g, ' ');
        q = q.or(`name.ilike.%${safe}%,location.ilike.%${safe}%`);
      }

      const rows = unwrap(await q);

      const { data: payments, error: payErr } = await supabase
        .from('client_payments')
        .select('project_id, amount_paise')
        .eq('approval_status', 'approved')
        .is('deleted_at', null);
      if (payErr) throw new Error(payErr.message);

      const paid = new Map<string, number>();
      for (const p of payments ?? []) {
        paid.set(p.project_id, (paid.get(p.project_id) ?? 0) + p.amount_paise);
      }

      return rows
        .map((r) => {
          const paidPaise = paid.get(r.id) ?? 0;
          return {
            id: r.id,
            name: r.name,
            location: r.location,
            status: r.status as ProjectStatus,
            startDate: r.start_date,
            valuePaise: r.value_paise,
            paidPaise,
            pendingPaise: Math.max(0, r.value_paise - paidPaise),
            clientId: r.client_id,
            clientName: (r.clients as unknown as { name: string } | null)?.name ?? '—',
          };
        })
        .sort((a, b) =>
          STATUS_RANK[a.status] - STATUS_RANK[b.status] || a.name.localeCompare(b.name));
    },
  });
}

/** For the client dropdown on the add-project form and filter toolbar. */
export function useClientOptions() {
  return useQuery({
    queryKey: ['client-options'],
    queryFn: async () => {
      const rows = unwrap(
        await supabase.from('clients').select('id, name').is('deleted_at', null).order('name'),
      );
      return rows as Array<{ id: string; name: string }>;
    },
    staleTime: 5 * 60_000,
  });
}

/** For project dropdowns across forms and filter toolbars. */
export function useProjectOptions(clientId?: string) {
  return useQuery({
    queryKey: ['project-options', clientId ?? ''],
    queryFn: async () => {
      let q = supabase
        .from('projects')
        .select('id, name, client_id')
        .is('deleted_at', null)
        .order('name');
      if (clientId) {
        q = q.eq('client_id', clientId);
      }
      const rows = unwrap(await q);
      return rows as Array<{ id: string; name: string; client_id: string }>;
    },
    staleTime: 5 * 60_000,
  });
}

export interface NewProject {
  client_id: string;
  name: string;
  location?: string;
  start_date?: string;
  status: ProjectStatus;
  valueRupees: string;
  description?: string;
}

export function useCreateProject() {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);

  return useMutation({
    mutationFn: async (input: NewProject) => {
      if (!actor) throw new Error('Not signed in');
      return unwrap(
        await supabase
          .from('projects')
          .insert({
            company_id: actor.companyId,
            client_id: input.client_id,
            name: input.name.trim(),
            location: input.location?.trim() || null,
            start_date: input.start_date || null,
            status: input.status,
            // Rupees typed by a person become integer paise at this boundary,
            // and stay integers for the rest of their life.
            value_paise: toPaise(input.valueRupees),
            description: input.description?.trim() || null,
            created_by: actor.id,
          })
          .select('id')
          .single(),
      );
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['projects'] }),
        qc.invalidateQueries({ queryKey: keys.dashboard }),
        qc.invalidateQueries({ queryKey: ['clients'] }),
        qc.invalidateQueries({ queryKey: ['client'] }),
        qc.invalidateQueries({ queryKey: ['lookups'] }),
      ]);
    },
  });
}

/* ----------------------------- client detail ----------------------------- */

export interface ClientDetail {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  address: string | null;
  city: string | null;
  totalProjects: number;
  ongoingProjects: number;
  completedProjects: number;
  projects: ProjectRow[];
}

/**
 * Deliberately no portfolio value and no bank details — the requirements remove
 * both from this page. The money shown is per project: value, paid, pending.
 */
export function useClient(clientId: string) {
  return useQuery({
    queryKey: keys.client(clientId),
    queryFn: async (): Promise<ClientDetail> => {
      const client = unwrap(
        await supabase
          .from('clients')
          .select('id, name, phone, email, address, city')
          .eq('id', clientId)
          .is('deleted_at', null)
          .single(),
      );

      const projectRows = unwrap(
        await supabase
          .from('projects')
          .select('id, name, location, status, start_date, value_paise, client_id')
          .eq('client_id', clientId)
          .is('deleted_at', null),
      );

      const ids = projectRows.map((p) => p.id);
      const paid = new Map<string, number>();
      if (ids.length) {
        const { data: payments, error } = await supabase
          .from('client_payments')
          .select('project_id, amount_paise')
          .in('project_id', ids)
          .eq('approval_status', 'approved')
          .is('deleted_at', null);
        if (error) throw new Error(error.message);
        for (const p of payments ?? []) {
          paid.set(p.project_id, (paid.get(p.project_id) ?? 0) + p.amount_paise);
        }
      }

      const projects: ProjectRow[] = projectRows
        .map((r) => {
          const paidPaise = paid.get(r.id) ?? 0;
          return {
            id: r.id,
            name: r.name,
            location: r.location,
            status: r.status as ProjectStatus,
            startDate: r.start_date,
            valuePaise: r.value_paise,
            paidPaise,
            pendingPaise: Math.max(0, r.value_paise - paidPaise),
            clientId: r.client_id,
            clientName: client.name,
          };
        })
        .sort((a, b) =>
          STATUS_RANK[a.status] - STATUS_RANK[b.status] || a.name.localeCompare(b.name));

      return {
        ...client,
        totalProjects: projects.length,
        ongoingProjects: projects.filter((p) => p.status === 'ongoing').length,
        completedProjects: projects.filter((p) => p.status === 'completed').length,
        projects,
      };
    },
    enabled: !!clientId,
  });
}

/* --------------------------- staff dashboard --------------------------- */

/**
 * Site staff get their own dashboard, not a censored version of the admin one.
 *
 * This deliberately queries NOTHING about client payments. Even filtered by
 * RLS, a partial payment total produces a wrong "pending" figure — which is
 * worse than showing none at all. What a site engineer needs is: my sites, and
 * what I have sent that is still waiting.
 */
export interface StaffDashboard {
  sites: Array<{ id: string; name: string; location: string | null; status: string }>;
  pendingCount: number;
  approvedThisWeek: number;
}

const STAFF_TABLES = [
  'material_purchases', 'material_usage', 'subcontractor_labour',
  'expenses', 'stock_transfers', 'client_payments',
] as const;

export function useStaffDashboard(userId: string | undefined) {
  return useQuery({
    queryKey: ['staff-dashboard', userId ?? ''],
    enabled: !!userId,
    queryFn: async (): Promise<StaffDashboard> => {
      const sites = unwrap(
        await supabase
          .from('projects')
          .select('id, name, location, status')
          .is('deleted_at', null)
          .order('name'),
      );

      const weekAgo = new Date(Date.now() - 7 * 86_400_000).toISOString();

      // One count query per table, run together. Counting server-side keeps the
      // payload tiny — we never pull the rows themselves.
      const counts = await Promise.all(
        STAFF_TABLES.flatMap((table) => [
          supabase.from(table).select('id', { count: 'exact', head: true })
            .eq('submitted_by', userId!).eq('approval_status', 'pending').is('deleted_at', null),
          supabase.from(table).select('id', { count: 'exact', head: true })
            .eq('submitted_by', userId!).eq('approval_status', 'approved')
            .gte('reviewed_at', weekAgo).is('deleted_at', null),
        ]),
      );

      let pendingCount = 0, approvedThisWeek = 0;
      counts.forEach((res, i) => {
        // A table the user cannot read returns an error rather than a count;
        // treat that as zero rather than failing the whole dashboard.
        const n = res.error ? 0 : res.count ?? 0;
        if (i % 2 === 0) pendingCount += n; else approvedThisWeek += n;
      });

      return { sites, pendingCount, approvedThisWeek };
    },
  });
}

/* --------------------------- edit and delete --------------------------- */

/**
 * Deletion is a soft delete — the row stays and gains a deleted_at stamp.
 *
 * In an accounting system a hard delete destroys the audit trail, and a client
 * removed by accident takes their whole payment history with them. Every list
 * filters on `deleted_at is null`, so a soft-deleted row disappears from the
 * product while remaining recoverable and still visible in the audit log.
 */

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: NewClient & { id: string }) => {
      const { error } = await supabase.from('clients').update({
        name: input.name.trim(),
        phone: input.phone.trim(),
        email: input.email?.trim() || null,
        address: input.address?.trim() || null,
        city: input.city?.trim() || null,
      }).eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['clients'] }),
        qc.invalidateQueries({ queryKey: ['client'] }),
        qc.invalidateQueries({ queryKey: ['client-options'] }),
        qc.invalidateQueries({ queryKey: ['lookups'] }),
      ]);
    },
  });
}

export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await deleteClientCascade(id);
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['clients'] }),
        qc.invalidateQueries({ queryKey: ['client'] }),
        qc.invalidateQueries({ queryKey: ['client-options'] }),
        qc.invalidateQueries({ queryKey: ['projects'] }),
        qc.invalidateQueries({ queryKey: ['project'] }),
        qc.invalidateQueries({ queryKey: ['client-payments'] }),
        qc.invalidateQueries({ queryKey: ['client-transactions'] }),
        qc.invalidateQueries({ queryKey: ['supplier-transactions'] }),
        qc.invalidateQueries({ queryKey: ['subcontractor-transactions'] }),
        qc.invalidateQueries({ queryKey: ['expense-transactions'] }),
        qc.invalidateQueries({ queryKey: ['project-drawings'] }),
        qc.invalidateQueries({ queryKey: ['reports'] }),
        qc.invalidateQueries({ queryKey: keys.dashboard }),
        qc.invalidateQueries({ queryKey: ['lookups'] }),
      ]);
    },
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: NewProject & { id: string }) => {
      const { error } = await supabase.from('projects').update({
        client_id: input.client_id,
        name: input.name.trim(),
        location: input.location?.trim() || null,
        start_date: input.start_date || null,
        status: input.status,
        value_paise: toPaise(input.valueRupees),
        description: input.description?.trim() || null,
      }).eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['projects'] }),
        qc.invalidateQueries({ queryKey: ['project'] }),
        qc.invalidateQueries({ queryKey: ['client'] }),
        qc.invalidateQueries({ queryKey: keys.dashboard }),
        qc.invalidateQueries({ queryKey: ['lookups'] }),
      ]);
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await deleteProjectCascade(id);
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['projects'] }),
        qc.invalidateQueries({ queryKey: ['project'] }),
        qc.invalidateQueries({ queryKey: ['clients'] }),
        qc.invalidateQueries({ queryKey: ['client'] }),
        qc.invalidateQueries({ queryKey: ['client-payments'] }),
        qc.invalidateQueries({ queryKey: ['client-transactions'] }),
        qc.invalidateQueries({ queryKey: ['supplier-transactions'] }),
        qc.invalidateQueries({ queryKey: ['subcontractor-transactions'] }),
        qc.invalidateQueries({ queryKey: ['expense-transactions'] }),
        qc.invalidateQueries({ queryKey: ['project-drawings'] }),
        qc.invalidateQueries({ queryKey: ['reports'] }),
        qc.invalidateQueries({ queryKey: keys.dashboard }),
        qc.invalidateQueries({ queryKey: ['lookups'] }),
      ]);
    },
  });
}
